import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";

import { DATE_FORMAT, TIME_FORMAT } from "shared/globals";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const defaultFormat = `${DATE_FORMAT} ${TIME_FORMAT} z`;

/**
 * Formats a date with a custom Day.js/Moment-compatible format string.
 * Falls back to the global default with timezone if a format string isn't provided.
 *
 * @example formatDate(date, "YY-MM-DD")
 *
 * @param {Date} date - JS data object.
 * @param {string?} format - Format string.
 */
export default function (date, format = defaultFormat) {
  return dayjs(date).format(format);
}

/**
 * Format numeric input as M.DD.YYYY while user types.
 * Only accepts numeric characters and auto-inserts dots at correct positions.
 * Accepts single or double digit month (e.g., "9.22.2026" or "12.22.2026")
 * @param {string} input - Raw numeric input string
 * @returns {string} Formatted display string (e.g., "9.22.2026")
 */
export function formatReleaseDateInput(input) {
  // Remove all non-numeric characters
  const numericOnly = input.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numericOnly.slice(0, 8);
  
  if (limited.length === 0) return '';
  if (limited.length <= 2) return limited; // M or MM (just month digits)
  
  // For 3+ digits, determine if month is 1-9 (single digit) or 10-12 (double digit)
  // Check if first 2 digits form a valid month (10-12) or first digit is valid (1-9)
  const firstTwoDigits = parseInt(limited.slice(0, 2), 10);
  const isDoubleDigitMonth = firstTwoDigits >= 10 && firstTwoDigits <= 12;
  
  if (isDoubleDigitMonth) {
    // MM.DD.YYYY format (month 10-12)
    if (limited.length <= 4) {
      return `${limited.slice(0, 2)}.${limited.slice(2)}`; // MM.DD
    }
    return `${limited.slice(0, 2)}.${limited.slice(2, 4)}.${limited.slice(4)}`; // MM.DD.YYYY
  } else {
    // M.DD.YYYY format (month 1-9)
    if (limited.length <= 3) {
      return `${limited.slice(0, 1)}.${limited.slice(1)}`; // M.DD
    }
    return `${limited.slice(0, 1)}.${limited.slice(1, 3)}.${limited.slice(3)}`; // M.DD.YYYY
  }
}

/**
 * Convert formatted date (M.DD.YYYY or MM.DD.YYYY) back to ISO 8601 datetime format (YYYY-MM-DDTHH:mm:ss.000Z).
 * @param {string} formattedDate - Formatted date string (e.g., "9.22.2026" or "12.22.2026")
 * @returns {string|null} ISO 8601 datetime string or null if invalid
 */
export function convertFormattedDateToISO(formattedDate) {
  const numericOnly = formattedDate.replace(/\D/g, '');
  
  // Must be 7 or 8 digits (MDDYYYY or MMDDYYYY)
  if (numericOnly.length < 7 || numericOnly.length > 8) return null;
  
  // Pad to 8 digits if needed (for single-digit month: M.DD.YYYY becomes MM.DD.YYYY)
  const padded = numericOnly.length === 7 ? '0' + numericOnly : numericOnly;
  
  const month = padded.slice(0, 2);
  const day = padded.slice(2, 4);
  const year = padded.slice(4, 8);
  
  // Validate date values
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);
  
  if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12 || yearNum < 1900 || yearNum > 2100) {
    return null;
  }
  
  // Return as ISO 8601 datetime format (YYYY-MM-DDTHH:mm:ss.000Z)
  // Set time to 00:00:00 UTC with milliseconds
  return `${year}-${month}-${day}T00:00:00.000Z`;
}

/**
 * Format ISO 8601 datetime string to display format (M.D.YYYY) without timezone conversion.
 * Extracts the date part directly from ISO string to avoid timezone shift issues.
 * @param {string} isoDatetime - ISO 8601 datetime string (e.g., "2026-09-24T00:00:00.000Z")
 * @returns {string} Display format string (e.g., "9.24.2026") or empty string if invalid
 */
export function formatISODatetimeToDisplay(isoDatetime) {
  if (!isoDatetime || typeof isoDatetime !== 'string') {
    return '';
  }
  
  // Extract YYYY-MM-DD from ISO string (e.g., "2026-09-24" from "2026-09-24T00:00:00.000Z")
  const dateMatch = isoDatetime.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return '';
  }
  
  const year = dateMatch[1];
  const month = dateMatch[2];
  const day = dateMatch[3];
  
  // Convert MM to M (remove leading zero for single-digit months)
  const monthNum = parseInt(month, 10);
  
  return `${monthNum}.${day}.${year}`;
}
